import React, { useState, useEffect, useRef, useContext, useMemo, useCallback } from 'react';
import shortid from 'shortid';
import isEqual from 'react-fast-compare';
import isUndefined from 'lodash/isUndefined';
import useLayoutEffect from './useIsomorphicLayoutEffect';
import useIsEqualMemoize from './useIsEqualMemoize';
import useFormField from './useFormField';
import useStateWithGetter from './useStateWithGetter';
import { FormContext, ArrayFieldApiContext, ArrayFieldStateContext } from '../Context';

const moveArrayIndex = (arr, from, to) => {
  const array = arr.slice();
  const startIndex = to < 0 ? array.length + to : to;
  const item = array.splice(from, 1)[0];

  array.splice(startIndex, 0, item);

  return array;
};

const getCurrentIndex = (arr, key) => arr.findIndex(({ key: fieldKey }) => key === fieldKey);

export default function useArrayField({ arrayFieldApiRef, arrayFieldStateRef, ...props }) {
  const context = useContext(FormContext);
  const isMounted = useRef(false);
  const itemRefs = useRef(new Map());
  const {
    formFieldContext,
    formFieldRef,
    render: renderFormField,
    ...restFormField
  } = useFormField({ ...props, type: 'array' });
  const {
    $showError,
    dataPath,
    error,
    field: { items: fieldSchema = {} },
    initialValue,
    reset,
    setValue,
    value,
  } = formFieldContext;

  const createInitialItems = () => {
    if (Array.isArray(initialValue)) {
      return initialValue.map((values) => ({
        key: shortid.generate(),
        values,
        initialValues: values,
      }));
    }

    return [];
  };

  const [initialItems, setInitialItems, getInitialItems] = useStateWithGetter(createInitialItems);
  const [items, setItems, getItems] = useStateWithGetter(initialItems);
  const [schema, setSchema] = useState(fieldSchema);

  // support focus on first error
  // eslint-disable-next-line no-param-reassign
  formFieldRef.current = useMemo(
    () => ({
      focus() {
        const formFieldControl = context.getField(dataPath);

        if (Array.isArray(formFieldControl.error)) {
          const errorIndex = formFieldControl.error.findIndex((itemError) => itemError != null);
          const fieldRef = itemRefs.current.get([...getItems()][errorIndex].key);

          if (fieldRef.current) {
            fieldRef.current.focusFirstError();
          }
        }
      },
    }),
    [dataPath]
  );

  const handleOnBeforeValidate = useCallback(async () => {
    const errors = await Promise.all(
      getItems().map(async ({ key }) => {
        const itemRef = itemRefs.current.get(key);

        if (itemRef?.current) {
          await itemRef.current.validate();

          return Object.keys(itemRef.current.errors).length > 0
            ? itemRef.current.errors
            : undefined;
        }

        return undefined;
      })
    );

    if (errors.filter(Boolean).length === 0) {
      return [];
    }

    return [
      {
        errors: {
          [dataPath]: errors,
        },
      },
    ];
  }, [dataPath]);

  const handleSubmit = useCallback(() => {
    getItems().forEach(({ key }) => {
      const itemRef = itemRefs.current.get(key);

      if (itemRef?.current) {
        itemRef.current.setCurrentTouched();
      }
    });
  });

  useLayoutEffect(() => {
    const formFieldControl = context.getField(dataPath);

    formFieldControl.subscribe('submit', handleSubmit);
    formFieldControl.subscribe('touched', handleSubmit);
    context.subscribe('beforevalidate', handleOnBeforeValidate);

    return () => {
      formFieldControl.unsubscribe('submit', handleSubmit);
      formFieldControl.subscribe('touched', handleSubmit);
      context.unsubscribe('beforevalidate', handleOnBeforeValidate);
    };
  }, [handleOnBeforeValidate]);

  const afterMount = (fn) => {
    if (!isMounted.current) {
      return () => {};
    }

    return fn;
  };

  // TODO: Figure out why before mounting the initialValue is undefined when nesting
  useEffect(
    afterMount(() => {
      const nextItems = createInitialItems();

      setInitialItems(nextItems);

      if (!context.stateRef.current.touchedValues.has(dataPath)) {
        setItems(nextItems);
      }
    }),
    [useIsEqualMemoize(initialValue)]
  );

  useEffect(() => {
    if (items !== getInitialItems()) {
      const nextValue = items.map(({ values }) => values);

      setValue(nextValue.length > 0 ? nextValue : undefined);
    } else {
      // reset all items
      for (const itemRef of itemRefs.current.values()) {
        if (itemRef?.current) {
          itemRef.current.reset();
        }
      }
    }
  }, [useIsEqualMemoize(items)]);

  useEffect(
    afterMount(() => {
      const currentValue = getItems().map(({ values }) => values);
      if (!isEqual(currentValue, value)) {
        const nextItems = [...value].map((values, index) => {
          if (!isUndefined(items[index])) {
            return {
              ...items[index],
              values,
            };
          }

          return {
            key: shortid.generate(),
            values,
            initialValues: values,
          };
        });
        setItems(nextItems);
      }
    }),
    [useIsEqualMemoize(value)]
  );

  useEffect(() => {
    setSchema(fieldSchema);
  }, [useIsEqualMemoize(fieldSchema)]);

  useEffect(() => {
    isMounted.current = true;

    return () => {
      isMounted.current = false;
    };
  }, []);

  const [arrayFieldApi] = useState({
    add() {
      const nextItems = [
        ...getItems(),
        {
          key: shortid.generate(),
          values: undefined,
          initialValues: undefined,
        },
      ];

      setItems(nextItems);
    },
    addWithInitialValues(initialValues) {
      const nextItems = [
        ...getItems(),
        {
          key: shortid.generate(),
          values: initialValues,
          initialValues,
        },
      ];

      setItems(nextItems);
    },
    move(key, nextIndex) {
      const currentIndex = getCurrentIndex(getItems(), key);
      const nextItems = moveArrayIndex(getItems(), currentIndex, nextIndex);

      setItems(nextItems);
    },
    moveUp(key) {
      const currentIndex = getCurrentIndex(getItems(), key);
      const nextIndex = currentIndex - 1;

      // at the top already
      if (nextIndex < 0) {
        return;
      }

      const nextItems = moveArrayIndex(getItems(), currentIndex, nextIndex);

      setItems(nextItems);
    },
    moveDown(key) {
      const currentIndex = getCurrentIndex(getItems(), key);
      const nextIndex = currentIndex + 1;

      // at the bottom already
      if (nextIndex >= getItems().length) {
        return;
      }

      const nextItems = moveArrayIndex(getItems(), currentIndex, nextIndex);

      setItems(nextItems);
    },
    remove(key) {
      const nextItems = [...getItems()].filter(({ key: fieldKey }) => key !== fieldKey);

      setItems(nextItems);
    },
    reset() {
      reset();
    },
    setInitialValues(key, initialValues) {
      const currentIndex = getCurrentIndex(getInitialItems(), key);
      const nextInitialItems = [...getInitialItems()];

      nextInitialItems[currentIndex] = {
        ...nextInitialItems[currentIndex],
        initialValues,
      };

      const nextInitialValue = nextInitialItems.map(({ initialValues: values }) => values);

      context.updateInitialValue(dataPath, nextInitialValue);
    },
    update(key, values) {
      const currentIndex = getCurrentIndex(getItems(), key);
      const nextItems = [...getItems()];

      nextItems[currentIndex] = {
        ...nextItems[currentIndex],
        values,
      };

      setItems(nextItems);
    },
  });

  const arrayFieldState = { $showError, items, itemRefs: itemRefs.current, error, schema };

  const render = (children) =>
    renderFormField(
      <ArrayFieldApiContext.Provider value={arrayFieldApi}>
        <ArrayFieldStateContext.Provider value={arrayFieldState}>
          {children}
        </ArrayFieldStateContext.Provider>
      </ArrayFieldApiContext.Provider>
    );

  /* eslint-disable no-param-reassign */
  if (arrayFieldApiRef) {
    arrayFieldApiRef.current = arrayFieldApi;
  }

  if (arrayFieldStateRef) {
    arrayFieldStateRef.current = arrayFieldState;
  }

  return {
    ...restFormField,
    arrayFieldApi,
    arrayFieldState,
    formFieldContext,
    formFieldRef,
    render,
  };
}
